import axios, { AxiosResponse } from "axios";
import { LoginCredentials, User, UserAndAddress } from "../@types/user";

const BASE_URL = "https://sodastreamapi.azurewebsites.net/users";


class UserService {

  async resendVerificationEmail(email: string): Promise<{ success: boolean; error: string | null }> {
    try {
      const response: AxiosResponse = await axios.post(`${BASE_URL}/resend-verification-email`, { email });
  
      if (response.status >= 200 && response.status < 300) {
        return { success: true, error: null };
      } else {
        console.error(`Error resending verification email - ${response.status}: ${response.statusText}`);
        return { success: false, error: `Error ${response.status}: ${response.statusText}` };
      }
    } catch (error: any) {
      console.error("Error resending verification email:", error.message);
      return { success: false, error: error.message };
    }
  }
  

  async getAllUsers(): Promise<{ data: User[] | null; error: string | null }> {
    try {
      const response: AxiosResponse<User[]> = await axios.get(BASE_URL);
      if (response.status >= 200 && response.status < 300) {
        const users: User[] = response.data;

        return { data: users, error: null };
      } else {
        const error = "Error fetching users";
        console.error(error);
        return { data: null, error };
      }
    } catch (error: any) {
      console.error("Error fetching users:", error);
      return { data: null, error };
    }
  }


  async getUserById(
    userId: number
  ): Promise<{ data: User | null; error: string | null }> {
    try {
      const response: AxiosResponse<User> = await axios.get(
        `${BASE_URL}/${userId}`
      );
      if (response.status >= 200 && response.status < 300) {
        const user: User = response.data;

        return { data: user, error: null };
      } else {
        const error = "Error fetching user by ID";
        console.error(error);
        return { data: null, error };
      }
    } catch (error: any) {
      console.error("Error fetching user by ID:", error);
      return { data: null, error };
    }
  }

  async getUserByEmailAddress(email: string): Promise<{ data: User | null; error: string | null }> {
    try {
      const response: AxiosResponse<User> = await axios.get(
        `${BASE_URL}/email/${email}`
      );

      if (response.status >= 200 && response.status < 300) {
        const user: User = response.data;
        return { data: user, error: null };
      } else {
        throw new Error(`Server responded with status: ${response.status}`);
      }
    } catch (error: any) {
      // Enhanced error logging
      console.error('Detailed error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      return { 
        data: null, 
        error: error.response?.data?.message || error.message || 'Unknown error occurred'
      };
    }
  }

async requestPasswordReset(email: string): Promise<{ success: boolean; error: string | null }> {
  try {
    const response: AxiosResponse = await axios.post(`${BASE_URL}/request-password-reset`, { email });
    
    if (response.status >= 200 && response.status < 300) {
      return { success: true, error: null };
    } else {
      console.error(`Error sending password reset request - ${response.status}: ${response.statusText}`);
      return { success: false, error: `Error ${response.status}: ${response.statusText}` };
    }
  } catch (error: any) {
    console.error("Error requesting password reset:", error.message);
    return { success: false, error: error.message };
  }
}

  async resetPassword(token: string | null, newPassword: string): Promise<{ data: any | null; error: string | null }> {
    try {
      const response: AxiosResponse<any> = await axios.post(
        `${BASE_URL}/reset-password`, 
        { token, newPassword }
      );
  
      if (response.status >= 200 && response.status < 300) {
        return { data: response.data, error: null };
      } else {
        const error = `Error resetting password - ${response.status}: ${response.statusText}`;
        console.error(error);
        return { data: null, error };
      }
    } catch (error: any) {
      console.error("Error resetting password:", error.message);
      return { data: null, error: error.message };
    }
  }
  
async createUser(
  data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    role: string;
    is_verified: boolean;
   
  }
): Promise<{ data: UserAndAddress | null; error: string | null }> {
  try {
    const response: AxiosResponse<UserAndAddress> = await axios.post(BASE_URL, data);
    if (response.status >= 200 && response.status < 300) {
      return { data: response.data, error: null };
    } else {
      const error = `Error creating user - ${response.status}: ${response.statusText}`;
      console.error(error);
      return { data: null, error };
    }
  } catch (error: any) {
    console.error("Error creating user:", error.message);
    if (error.response) {
      console.error("Response data:", error.response.data);
    }
    return { data: null, error: error.message };
  }
}

 // Method to create a new user
 async createNewUser(
  userData: any
): Promise<{ data: User | null; error: string | null }> {
  try {
    const response: AxiosResponse<User> = await axios.post(
      BASE_URL,
      userData
    );
    if (response.status >= 200 && response.status < 300) {
      const createdUser: User = response.data;
      return { data: createdUser, error: null };
    } else {
      const error = `Error creating user - ${response.status}: ${response.statusText}`;
      console.error(error);
      return { data: null, error };
    }
  } catch (error: any) {
    console.error("Error creating user:", error.message);
    // Log additional information if available
    if (error.response) {
      console.error("Response data:", error.response.data);
  
    }
    return { data: null, error: error.message };
  }
}

  async editUser(
    userId: number,
    updatedUserData: any
  ): Promise<{ data: User | null; error: string | null }> {
    try {
      const response: AxiosResponse<User> = await axios.put(
        `${BASE_URL}/${userId}`,
        updatedUserData
      );
      if (response.status >= 200 && response.status < 300) {
        const updatedUser: User = response.data;
   
        return { data: updatedUser, error: null };
      } else {
        const error = `Error updating user - ${response.status}: ${response.statusText}`;
        console.error(error);
        return { data: null, error };
      }
    } catch (error: any) {
      console.error("Error updating user:", error.message);
     
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
        console.error("Response headers:", error.response.headers);
      }
      return { data: null, error: error.message };
    }
  }

  async deleteUser(
    userId: number
  ): Promise<{ data: any | null; error: string | null }> {
    try {
      const response: AxiosResponse<any> = await axios.delete(
        `${BASE_URL}/${userId}`
      );

      if (response.status >= 200 && response.status < 300) {
      
        return { data: response.data, error: null };
      } else {
        const error = `Error deleting user - ${response.status}: ${response.statusText}`;
        console.error(error);
        return { data: null, error };
      }
    } catch (error: any) {
      console.error("Error deleting user:", error.message);
    
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
        console.error("Response headers:", error.response.headers);
      }
      return { data: null, error: error.message };
    }
  }

  async getUserByEmail(
    email: string
  ): Promise<{
    role?: string; data: User | null; error: string | null 
}> {
    try {
      const response: AxiosResponse<User> = await axios.get(`${BASE_URL}/email/${email}`);
      if (response.status >= 200 && response.status < 300) {
        const user: User = response.data;

        return { data: user, error: null };
      } else {
        const error = `Error fetching user by email - ${response.status}: ${response.statusText}`;
        console.error(error);
        return { data: null, error };
      }
    } catch (error: any) {
      console.error("Error fetching user by email:", error.message);
      if (error.response) {
        console.error("Response data:", error.response.data);
      }
      return { data: null, error: error.message };
    }
  }


async loginUser(
  credentials: LoginCredentials
): Promise<{ data: User | null; error: string | null }> {
  try {
    const response: AxiosResponse<User> = await axios.post(
      `${BASE_URL}/login`, 
      credentials
    );

    if (response.status >= 200 && response.status < 300) {
      const user: User = response.data;

      return { data: user, error: null };
    } else {
      const error = `Error logging in - ${response.status}: ${response.statusText}`;
      console.error(error);
      return { data: null, error };
    }
  } catch (error: any) {
    console.error("Error logging in:", error.message);
    if (error.response) {
      console.error("Response data:", error.response.data);
      console.error("Response status:", error.response.status);
      console.error("Response headers:", error.response.headers);
    }
    return { data: null, error: error.message };
  }
}



}

const userServiceInstance = new UserService();
export default userServiceInstance;

